/**
 * RepliesListDialog.js
 *
 * Drill-down for the "Total Replies" KPI on the dashboard Overview tab.
 * Lists every reply behind the KPI (POST user-dashboard get-replies-list)
 * with search and pagination. The eye button on each row opens the actual
 * reply content in a second dialog, rendered inside a sandboxed iframe so
 * email markup cannot leak styles or run scripts in the app.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import IconComponent from "../../../components/Icon/IconComponent";
import EventEmitter from "../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../../utils/constants";
import { ICON_LABELS } from "../../../utils/constants/UI";
import { postRequest } from "../../../Apis";
import { dashboardEndpoints } from "../../../utils/constants/httpConstants";
import { decompressString } from "../../../utils/helpers";
import { CSS_VARS } from "../newDashboardData";
import styles from "../NewDashboard.module.css";

const PAGE_LIMIT = 10;

const TABLE_HEADS = [
  "Contact",
  "Category",
  "Company",
  "Campaign",
  "Subject",
  "Replied",
  "",
];

// `auto_reply_family` from get-replies-list, in the words a reader wants.
// Families that only say "a machine wrote this" without saying which kind
// (`subject`, `auto_ack`) both read as the plain "Auto-reply".
const REPLY_CATEGORY_LABELS = {
  subject: "Auto-reply",
  auto_ack: "Auto-reply",
  ooo_absence: "Out of office",
  dead_mailbox: "Mailbox dead",
  person_gone: "Person left",
  business_closed: "Business closed",
  challenge: "Verification",
};

// Three states, not two. A row with no verdict at all is UNKNOWN, not human —
// the same trap the open-tracking flags document: an unflagged row has simply
// never been classified. This is what lets the column ship before the backend
// that fills it in, and keeps old cached responses from claiming every reply
// was written by a person.
const replyCategoryCell = (row) => {
  if (typeof row?.is_auto_reply !== "boolean") return "-";
  if (!row.is_auto_reply) {
    return <span className={styles.typeChipHuman}>Human</span>;
  }
  return (
    <span
      className={styles.typeChipAuto}
      title={
        row.auto_reply_evidence
          ? `Detected from: "${row.auto_reply_evidence}"`
          : undefined
      }
    >
      {REPLY_CATEGORY_LABELS[row.auto_reply_family] || "Auto-reply"}
    </span>
  );
};

const formatReplyDate = (ts) =>
  ts
    ? new Date(ts).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const contactName = (row) =>
  [row?.first_name, row?.last_name].filter(Boolean).join(" ") ||
  row?.email ||
  "-";

// reply_html_payload comes back zlib+base64 compressed and URL-encoded
// (same format the campaign StatsDialog reply viewer handles). Decode it to
// renderable HTML; if it's already plain HTML (or decode fails), use as-is.
const looksCompressed = (str = "") =>
  typeof str === "string" &&
  str.length > 40 &&
  /^[A-Za-z0-9+/=]+$/.test(str) &&
  !/<[a-z!/]/i.test(str);

const decodeReplyHtml = (payload = "") => {
  if (!payload) return "";
  if (!looksCompressed(payload)) return payload;
  const decompressed = decompressString(payload);
  if (!decompressed) return payload;
  try {
    return decodeURIComponent(decompressed);
  } catch (e) {
    return decompressed;
  }
};

// A stored payload can be a full HTML document whose body holds no visible
// text at all (e.g. an Outlook subject-only reply: one empty <div><br></div>).
// Treat images as content; otherwise strip tags/entities and look for text.
const hasVisibleContent = (html = "") => {
  if (!html) return false;
  if (/<img[\s>]/i.test(html)) return true;
  const text = html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<head[\s\S]*?<\/head>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&#160;|&zwnj;|&zwj;/gi, " ")
    .replace(/\s+/g, "");
  return text.length > 0;
};

/** Second-level dialog - shows the full reply content for one row */
const ReplyViewDialog = ({ reply, onClose }) => {
  const row = reply?.row || {};
  const html = reply?.html || "";
  const name = contactName(row);
  const isEmptyMessage = Boolean(html) && !hasVisibleContent(html);

  // Sandboxed document: no scripts, links open in a new tab.
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8" /><base target="_blank" /><style>body{font-family:'Inter','Segoe UI',Arial,sans-serif;font-size:13.5px;line-height:1.6;margin:16px 18px;word-break:break-word;}</style></head><body>${html}</body></html>`;

  const metaItems = [
    { label: "Contact", value: name, sub: row.email },
    { label: "Company", value: row.company_name || "-", sub: row.job_title },
    { label: "Campaign", value: row.campaign_name || "-" },
    { label: "Replied To", value: row.from_email || "-" },
    { label: "Reply Date", value: formatReplyDate(row.reply_date) },
    { label: "Replies In Thread", value: String(row.reply_count || 1) },
  ];

  return (
    <CustomDialog
      open={Boolean(reply)}
      onClose={onClose}
      width="46vw"
      remove_padding
      header={{ heading: { label: `Reply from ${name}` } }}
    >
      <div className={styles.replyViewRoot} style={CSS_VARS}>
        <div className={styles.replyMetaGrid}>
          {metaItems.map((item, i) => (
            <div className={styles.replyMetaItem} key={i}>
              <div className={styles.replyMetaLabel}>{item.label}</div>
              <div className={styles.replyMetaValue}>{item.value}</div>
              {item.sub ? (
                <div className={styles.cellSub}>{item.sub}</div>
              ) : null}
            </div>
          ))}
        </div>
        <div className={styles.replySubjectRow}>
          <div className={styles.replyMetaLabel}>Subject</div>
          <div className={styles.replySubjectText}>
            {row.subject || "(No subject)"}
          </div>
        </div>
        {html && !isEmptyMessage ? (
          <div className={styles.replyBodyWrap}>
            <iframe
              title="Reply content"
              className={styles.replyFrame}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={srcDoc}
            />
          </div>
        ) : (
          <div className={styles.replyBodyEmpty}>
            {isEmptyMessage
              ? "The sender sent an empty message (no text in the email body — likely a subject-only reply)"
              : "No reply content available for this record"}
          </div>
        )}
      </div>
    </CustomDialog>
  );
};

const RepliesListDialog = ({ open, onClose, dateRange, navigate }) => {
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState([]);
  const [meta, setMeta] = useState(null);
  const [viewedReply, setViewedReply] = useState(null); // { row, html }
  const [replyLoadingId, setReplyLoadingId] = useState(null);
  // record_id -> reply_html_payload, so re-opening a reply is instant
  const bodiesRef = useRef({});

  const buildPayload = useCallback(
    (extra = {}) => {
      const payload = { page, limit: PAGE_LIMIT, ...extra };
      if (dateRange?.from && dateRange?.to) {
        payload.date_range = { from: dateRange.from, to: dateRange.to };
      }
      if (search) payload.search = search;
      return payload;
    },
    [page, search, dateRange],
  );

  // Reset everything when the dialog closes so each open starts fresh
  useEffect(() => {
    if (!open) {
      setPage(0);
      setSearchInput("");
      setSearch("");
      setReplies([]);
      setMeta(null);
      setViewedReply(null);
      setReplyLoadingId(null);
      bodiesRef.current = {};
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const fetchReplies = async () => {
      setLoading(true);
      try {
        const response = await postRequest(
          navigate,
          dashboardEndpoints.getRepliesList,
          buildPayload(),
        );
        if (cancelled) return;
        setReplies(response?.data?.data?.replies || []);
        setMeta(response?.data?.data?.meta || null);
      } catch (err) {
        if (cancelled) return;
        EventEmitter.emit(
          EMITTER_ERROR,
          err?.data?.message || "Failed to fetch replies list",
        );
        setReplies([]);
        setMeta(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReplies();
    return () => {
      cancelled = true;
    };
  }, [open, buildPayload, navigate]);

  const cacheBodies = (rows = []) => {
    rows.forEach((r) => {
      if (r?.record_id) {
        bodiesRef.current[r.record_id] = decodeReplyHtml(r.reply_html_payload);
      }
    });
  };

  const handleViewReply = async (row) => {
    if (replyLoadingId) return;

    const cached = bodiesRef.current[row.record_id];
    if (cached !== undefined) {
      setViewedReply({ row, html: cached });
      return;
    }

    setReplyLoadingId(row.record_id);
    try {
      // Refetch the current page with bodies included - caches the whole
      // page so the next eye click on this page opens instantly.
      let response = await postRequest(
        navigate,
        dashboardEndpoints.getRepliesList,
        buildPayload({ include_reply_body: true }),
      );
      cacheBodies(response?.data?.data?.replies);

      if (bodiesRef.current[row.record_id] === undefined) {
        // Row shifted off this page (a newer reply arrived) - target it
        // directly by email and campaign instead.
        response = await postRequest(
          navigate,
          dashboardEndpoints.getRepliesList,
          {
            page: 0,
            limit: 50,
            search: row.email,
            campaign_ids: row.campaign_id ? [row.campaign_id] : [],
            include_reply_body: true,
          },
        );
        cacheBodies(response?.data?.data?.replies);
      }

      const html = bodiesRef.current[row.record_id];
      if (html === undefined) {
        EventEmitter.emit(
          EMITTER_ERROR,
          "Could not load this reply, please try again",
        );
        return;
      }
      setViewedReply({ row, html });
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        err?.data?.message || "Failed to fetch reply content",
      );
    } finally {
      setReplyLoadingId(null);
    }
  };

  const totalPages = meta?.total_pages || 1;
  const totalReplies = meta?.total || 0;

  return (
    <>
      <CustomDialog
        open={open}
        onClose={onClose}
        width="72vw"
        remove_padding
        header={{ heading: { label: "Total Replies" } }}
      >
        <div className={styles.repliesRoot} style={CSS_VARS}>
          {/* Toolbar */}
          <div className={styles.repliesToolbar}>
            <div className={styles.repliesSearch}>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, email, or company"
              />
            </div>
            {meta ? (
              <div className={styles.repliesCount}>
                {totalReplies.toLocaleString()}{" "}
                {totalReplies === 1 ? "reply" : "replies"}
              </div>
            ) : null}
          </div>

          {/* Table */}
          <div className={styles.repliesTableWrap}>
            {!loading && !replies.length ? (
              <div className={styles.emptyMeetings}>
                <div className={styles.emptyMeetingsIcon}>
                  <IconComponent
                    iconLabel={ICON_LABELS.EMAIL}
                    fontSize="28px"
                  />
                </div>
                <div className={styles.emptyMeetingsTitle}>
                  No replies found
                </div>
                <div className={styles.emptyMeetingsSubtitle}>
                  {search
                    ? "Try a different search or clear the filter"
                    : "Replies to your campaigns will appear here"}
                </div>
              </div>
            ) : (
              <table className={styles.tbl}>
                <thead>
                  <tr>
                    {TABLE_HEADS.map((h, i) => (
                      <th key={i}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <tr key={`skeleton-${i}`}>
                          {TABLE_HEADS.map((_h, j) => (
                            <td key={j}>
                              <div
                                className={styles.skelBar}
                                style={{ width: `${55 + ((i + j) % 3) * 15}%` }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    : replies.map((r) => (
                        <tr key={r.record_id}>
                          <td>
                            <div className={styles.cellMain}>
                              {contactName(r)}
                            </div>
                            <div className={styles.cellSub}>{r.email}</div>
                          </td>
                          <td>{replyCategoryCell(r)}</td>
                          <td>
                            <div>{r.company_name || "-"}</div>
                            {r.job_title ? (
                              <div className={styles.cellSub}>
                                {r.job_title}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <div>{r.campaign_name || "-"}</div>
                          </td>
                          <td>
                            <div
                              className={styles.subjectCell}
                              title={r.subject}
                            >
                              {r.subject || "-"}
                            </div>
                          </td>
                          <td>
                            <div className={styles.repliedCell}>
                              {formatReplyDate(r.reply_date)}
                              {r.reply_count > 1 ? (
                                <span
                                  className={styles.replyCountPill}
                                  title={`${r.reply_count} replies in this thread`}
                                >
                                  x{r.reply_count}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className={styles.eyeBtn}
                              onClick={() => handleViewReply(r)}
                              disabled={Boolean(replyLoadingId)}
                              title="View reply"
                              aria-label="View reply"
                            >
                              {replyLoadingId === r.record_id ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <IconComponent
                                  iconLabel={ICON_LABELS.VISIBILITY_ON}
                                  fontSize="16px"
                                />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 ? (
            <div className={styles.repliesPagination}>
              <button
                className={styles.pageBtn}
                disabled={page === 0 || loading}
                onClick={() => setPage(page - 1)}
              >
                Prev
              </button>
              <span className={styles.pageInfo}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages - 1 || loading}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </CustomDialog>

      <ReplyViewDialog
        reply={viewedReply}
        onClose={() => setViewedReply(null)}
      />
    </>
  );
};

export default RepliesListDialog;
